import type { ComponentType, ReactNode } from "react";
import {
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  Info,
  ShieldOff,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAudit } from "../../../../contexts/auditContext";
import { useAnalyticsStatus } from "../../../../hooks/useAnalyticsWorkflows";
import { assertUnhandledWorkflowStatus } from "../../../../lib/analyticsWorkflow";
import { sessionRouteBase } from "../../../../lib/sessionApiBase";
import { cn } from "../../../../lib/utils";
import type { AnalyticsColumn } from "../../../../models/audit/analyticsColumn";
import type {
  InvalidOutputCause,
  WorkflowStatus,
} from "../../../../models/audit/analyticsEnums";
import type { AnalyticsInvalidOutput } from "../../../../models/audit/analyticsWorkflow";
import type { SessionKind } from "../../../../hooks/audit/useControlSessionEnvelope";
import { useSessionAnalyticsOwner } from "../../../../hooks/audit/useSessionAnalyticsOwner";

/**
 * What an analytics column's derived status says to the auditor, and what — if
 * anything — they can do about it.
 *
 * **The copy is not declared here.** Every wording arrives on the wire as
 * `statusCopy`, authored once in the backend's analytics copy module beside the
 * status enum, because the work-paper export is a Python consumer of the same
 * strings and could not read a constant that lived in this file. Four wordings
 * of one message is the failure that arrangement prevents, so nothing below
 * substitutes a sentence for a status.
 *
 * The one thing this file does author is the *cause* line under
 * `invalid_output`, which has no server-side counterpart: the copy module maps
 * one string per status, and `invalid_output` is a five-cause bucket whose
 * causes send the auditor to five different places. Should the export ever need
 * to write a cause, these labels move to that module and this map goes away.
 *
 * The second and last exception is the line for a status read that failed,
 * which cannot come from the server for the reason recorded beside it.
 */

/**
 * Interim: the deep link bounces through AuditBoard, and an auditor with no
 * Optro session lands on a sign-in first. One constant, rendered in one place,
 * so removing the caveat once the bounce carries the session through is a
 * single deletion.
 */
const OPTRO_SIGN_IN_CAVEAT = "You may need to sign in to Optro first.";

/** Names the destination rather than the remedy — the remedy is `statusCopy`'s. */
const OPTRO_CTA_LABEL = "Open Optro Analytics";

/** Names the tab the fix is made on; what to fix there is `statusCopy`'s. */
const SUPPORT_TAB_CTA_LABEL = "Fix in Support tab";

/**
 * The one wording this file authors for a status read that did not answer.
 *
 * A deliberate exception to the rule above, not an oversight: `statusCopy` is
 * the server's account of a status it derived, and a failed read means the
 * server said nothing at all — so there is no response to carry copy for its
 * own silence, and the copy module has nothing to key it off. It belongs on the
 * side that observed the failure, which is this one.
 *
 * The error's own text is never rendered beside it. `schema.parse()` throws a
 * `ZodError` whose message is a JSON dump of the payload and a dropped request
 * reads "Failed to fetch"; the failure is a signal, not copy.
 */
export const STATUS_UNREADABLE_COPY =
  "This column's analytics status could not be read.";

/** Which of `invalid_output`'s five causes fired, in the auditor's terms. */
const INVALID_OUTPUT_CAUSE_LABEL: Record<InvalidOutputCause, string> = {
  undeclared_key:
    "Input keys bound here that the workflow's activated version does not declare",
  non_file_key:
    "Input keys bound here that name a parameter which is not a file",
  run_failed: "The run itself failed",
  unclassifiable_run_state:
    "The run ended in a lifecycle state Midship cannot classify",
  unresolved_outputs: "The run's outputs could not be resolved into a manifest",
};

type BannerTone = "muted" | "warning" | "blocked";

const TONE_CLASSES: Record<BannerTone, string> = {
  muted: "border-stone-300 bg-stone-50 text-stone-700",
  warning: "border-amber-300 bg-amber-50 text-amber-900",
  blocked: "border-stone-400 bg-stone-100 text-stone-800",
};

/**
 * The way to a status's remedy, where the auditor has one to take.
 *
 * Two destinations and not one, because they are not the same kind of place:
 * the builder is the workflow's home in Optro Analytics, reached by a URL the
 * backend composes, and the Support tab is this app's own route, reached by the
 * router. An internal destination rendered as an external anchor would reload
 * the app to arrive somewhere it was already standing, and would mark an in-app
 * page with an out-of-app affordance.
 */
type StatusRemedy = "optro_builder" | "support_tab" | "none";

type StatusPresentation = {
  tone: BannerTone;
  icon: ComponentType<{ className?: string }>;
  /**
   * How the auditor reaches this status's remedy, or `none` where there is no
   * remedy of theirs to reach.
   *
   * `none` for `not_entitled` however loudly it reads like the deleted case:
   * the workflow is fine, the workspace's credential is not, and only an
   * administrator can re-enable it. Offering a link there invites an auditor to
   * go fix something they have no way to fix.
   */
  remedy: StatusRemedy;
};

/**
 * How a status draws, or `null` for the ones that draw nothing.
 *
 * Exhaustive by construction: a status added to the enum turns this into a
 * compile error until someone decides how it reads, and the `default` arm
 * covers the case a compile-time check cannot — a payload that got past the
 * schema — rather than letting it fall through to another status's treatment.
 */
function presentationFor(status: WorkflowStatus): StatusPresentation | null {
  switch (status) {
    case "ok":
      return null;
    case "not_configured":
      // The builder link exists only where a shell is already built and owes a
      // publish. A column that never had a workflow carries no URL of either
      // kind, and its remedy is the configure action, which lives in the column
      // dialog rather than here — so that arm renders the copy and no link.
      return { tone: "muted", icon: Info, remedy: "optro_builder" };
    case "not_run":
      // Derived wherever the column's latest run is absent or not yet terminal
      // — the ordinary state of a new work paper, and of a re-run in flight
      // over cells still holding earlier verdicts — not a condition to act on.
      // It draws nothing for the same reason `ok` does: the cells already say
      // it, empty before a first run and drawn as running while a Generate
      // over them is in flight, and a banner repeating it down the table is
      // noise over a grid with nothing wrong with it.
      return null;
    case "invalid_output":
      // The remedy is inside Midship — the column's inputs and its run are the
      // control session's Support tab — so this one routes in rather than out.
      return { tone: "warning", icon: AlertTriangle, remedy: "support_tab" };
    case "workflow_deleted":
      // No link yet. Neither URL survives the deletion: the published workflow
      // is gone, and the builder link belongs to a shell still awaiting its
      // publish. Attaching another workflow is the configure action, which this
      // banner does not host.
      return { tone: "warning", icon: AlertTriangle, remedy: "none" };
    case "not_entitled":
      return { tone: "blocked", icon: ShieldOff, remedy: "none" };
    default:
      return assertUnhandledWorkflowStatus(status);
  }
}

function invalidOutputDetail(
  invalidOutput: AnalyticsInvalidOutput | null,
): string | null {
  if (!invalidOutput) return null;
  const label = INVALID_OUTPUT_CAUSE_LABEL[invalidOutput.cause];
  return invalidOutput.keys.length > 0
    ? `${label}: ${invalidOutput.keys.join(", ")}`
    : `${label}.`;
}

interface AnalyticsStatusBannerProps {
  /** Absent or `false` where the status read answered. */
  unreadable?: false;
  status: WorkflowStatus;
  /** The wording for `status`, authored server-side and rendered verbatim. */
  statusCopy: string;
  /** Present exactly when `status` is `invalid_output`. */
  invalidOutput?: AnalyticsInvalidOutput | null;
  /**
   * The bound workflow's id, rendered wherever the banner names the workflow.
   *
   * The id and never a name: the name the column stores is the free text
   * captured when the column was authored, which an upstream rename leaves
   * behind, and the live lookup that would supply the real one is the call
   * answering `410 Gone` in the state that most needs to name it. An id names
   * the workflow an administrator has to go find either way.
   */
  workflowId?: number | null;
  /**
   * The AuditBoard bounce into the Cascade builder, for a shell that owes a
   * publish. Absent means no call to action rather than a dead link.
   *
   * Never the published-workflow URL: that one is null in exactly the states
   * whose remedy is out in Optro, so a call to action driven from it is
   * suppressed every time it is needed.
   */
  builderUrl?: string | null;
  /**
   * The session whose Support tab holds `invalid_output`'s remedy, and which
   * route family it lives under. Absent means that action is not offered,
   * rather than a route with a hole where the id goes.
   */
  session?: { id: string; kind: SessionKind } | null;
}

/** The status read that did not answer: one line, and no claim about the column. */
interface AnalyticsStatusUnreadableProps {
  unreadable: true;
}

export type AnalyticsNeedsSetupBannerProps =
  | AnalyticsStatusBannerProps
  | AnalyticsStatusUnreadableProps;

/**
 * The banner's chrome, so the two things it can say are framed identically.
 */
function BannerShell({
  tone,
  icon: Icon,
  children,
}: {
  tone: BannerTone;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      role="status"
      className={cn(
        "mt-2 rounded border px-2 py-1.5 text-xs font-normal",
        TONE_CLASSES[tone],
      )}
    >
      <div className="flex items-start gap-1.5">
        <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="min-w-0 space-y-1">{children}</div>
      </div>
    </div>
  );
}

/**
 * The needs-setup banner for one analytics column.
 *
 * Keyed off the status value, not off a `needsSetup` predicate. The backend's
 * `needs_setup()` deliberately excludes `not_entitled` — "nobody's setup to
 * finish" — so a banner gated on that boolean would render nothing for it and
 * leave its copy unreachable. Every status gets an arm here and the predicate,
 * wherever it resolves, cannot silence one.
 *
 * `unreadable` is the read that failed, and it carries no status because there
 * is none: `ok` and `not_run` render nothing by design, so a failed read left
 * to fall through would draw exactly what a healthy column draws. Its tone is
 * `muted` and not `warning` — amber is this work paper's colour for a column
 * in trouble, and Midship failing to read a status is not a finding about the
 * control.
 */
export const AnalyticsNeedsSetupBanner = (
  props: AnalyticsNeedsSetupBannerProps,
): JSX.Element | null => {
  if (props.unreadable === true) {
    return (
      <BannerShell tone="muted" icon={Info}>
        <p className="whitespace-normal">{STATUS_UNREADABLE_COPY}</p>
      </BannerShell>
    );
  }

  const {
    status,
    statusCopy,
    invalidOutput = null,
    workflowId = null,
    builderUrl = null,
    session = null,
  } = props;

  const presentation = presentationFor(status);
  if (!presentation) return null;

  const detail =
    status === "invalid_output" ? invalidOutputDetail(invalidOutput) : null;
  const optroUrl = presentation.remedy === "optro_builder" ? builderUrl : null;
  const supportTabPath =
    presentation.remedy === "support_tab" && session !== null
      ? `${sessionRouteBase(session.kind)}/${session.id}/`
      : null;
  const namesWorkflow = status === "workflow_deleted" && workflowId !== null;

  return (
    <BannerShell tone={presentation.tone} icon={presentation.icon}>
      <p className="whitespace-normal">{statusCopy}</p>
      {namesWorkflow && <p className="opacity-80">Workflow {workflowId}</p>}
      {detail && <p className="whitespace-normal">{detail}</p>}
      {optroUrl !== null && (
        <>
          <a
            href={optroUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium underline underline-offset-2"
          >
            {OPTRO_CTA_LABEL}
            <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
          <p className="opacity-80">{OPTRO_SIGN_IN_CAVEAT}</p>
        </>
      )}
      {supportTabPath !== null && (
        <Link
          to={supportTabPath}
          className="inline-flex items-center gap-1 font-medium underline underline-offset-2"
        >
          {SUPPORT_TAB_CTA_LABEL}
          <ArrowRight className="h-3 w-3 shrink-0" />
        </Link>
      )}
    </BannerShell>
  );
};

export interface AnalyticsColumnStatusBannerProps {
  analyticsColumn: AnalyticsColumn;
}

/**
 * The banner wired to a column's status read, for the column header.
 *
 * The header is the right grain and the only one. A deleted workflow and a
 * disabled credential are facts about the whole column — no cell in it can be
 * produced — so they are said once, where the column is named, rather than
 * repeated down every sample row. What a *cell* shows is untouched by this: a
 * column whose workflow was deleted after a successful run still renders the
 * verdicts it stored, because the verdict is Midship's and only the evidence
 * behind it is gone, and an unrun cell still renders empty exactly as an
 * agent-backed attribute test's does.
 *
 * `not_run` draws nothing here. It stands for a latest run that is absent or
 * not yet terminal — a new work paper, or a re-run in flight — rather than a
 * setup failure, and the cells already say so: empty before a first run, drawn
 * as running while a Generate over them is in flight.
 *
 * Nothing here infers deletion from a run's reads. Deletion upstream cascades
 * to the workflow's runs, and the routes reading its instance and runs then
 * fail as `upstream_unavailable`, as they do in an engine outage. The status,
 * the backend's reading of the workflow resource itself, is the only report.
 *
 * An in-flight status read renders nothing: the column's results are unaffected
 * by it, and a banner about a request still in the air would be noise over the
 * top of a grid that is working. A *failed* read says so in one muted line,
 * because `ok` and `not_run` are the statuses that render nothing — so a
 * failure left to fall through would draw a healthy column.
 */
export const AnalyticsColumnStatusBanner = ({
  analyticsColumn,
}: AnalyticsColumnStatusBannerProps): JSX.Element | null => {
  const { controlSession, sessionKind } = useAudit();
  const owner = useSessionAnalyticsOwner();
  const { data, error } = useAnalyticsStatus(owner, analyticsColumn.id);
  if (error !== null) return <AnalyticsNeedsSetupBanner unreadable />;
  // Absent rather than failed: the read is disabled without an owner, and a
  // failure notice for a request that never went out is its own false claim.
  if (!data) return null;

  return (
    <AnalyticsNeedsSetupBanner
      status={data.status}
      statusCopy={data.statusCopy}
      invalidOutput={data.invalidOutput}
      workflowId={analyticsColumn.workflowBinding?.automationWorkflowId ?? null}
      // The same links the review pane offers. Without them the arms that have
      // a remedy — a shell that owes a publish, and a run whose output the
      // Support tab can put right — describe one and withhold the way to it,
      // and the header is where a column-grain state is said.
      builderUrl={data.builderUrl}
      session={
        controlSession ? { id: controlSession.id, kind: sessionKind } : null
      }
    />
  );
};
